import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  makeStyles,
  Snackbar,
  SnackbarContent
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { formatHtmlForExtraction, getSFUserList, isRunningInIframe } from '../lib/Global'
import {
  getLineItemTypes,
  getLocations,
  getProposal,
  getSitesAdvancedFiltersInfo,
  updateProposal
} from '../services/ApiService'
import { ProposalTimeline } from '../components/proposals/ProposalTimeline'
import { ProposalHeaderComponent } from '../components/proposals/ProposalHeader'
import { ProposalItems } from '../components/proposals/ProposalItems'
import { nanoid } from '@reduxjs/toolkit'
import { getCompanyUsers } from '../lib/Api'
import { useDispatch, useSelector } from 'react-redux'
import { isEqual } from 'lodash'
import { editableProposalStatus } from '../lib/Constants'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { loadingActions } from '../store/loading'

const useStyles = makeStyles(theme => ({
  scrollContainer: {
    margin: '0px',
    padding: '0px',
    maxWidth: '100%',
    overflowY: 'auto'
  },
  container: {
    backgroundColor: theme.colors.mainContainerBackground,
    paddingLeft: '0px',
    paddingRight: '0px',
    margin: '0px',
    maxWidth: '100%',
    minWidth: 'fit-content'
  },
  globalHeaderContainer: {
    height: '72px',
    backgroundColor: 'white',
    maxWidth: '100%',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  mainContainer: {
    display: 'flex',
    backgroundColor: 'white',
    maxWidth: '100%',
    paddingBottom: '50px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0'
    },
    minHeight: '100vh'
  },
  leftContainer: {
    paddingRight: 0,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: '0'
    }
  },
  itemsContainer: {
    padding: '0px 20px'
  },
  rightContainer: {
    width: '360px',
    padding: '0'
  },
  bottomContainer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'white',
    padding: '10px',
    boxShadow: '0 -2px 5px rgba(0, 0, 0, 0.1)'
  },
  backButton: {
    fontSize: '14px',
    fontWeight: '500',
    height: '58px',
    width: '105px',
    textTransform: 'none',
    marginLeft: '68px',
    boxShadow: '6px 9px 43px 0px ' + theme.colors.invoiceColors.shadow,
    [theme.breakpoints.down('xs')]: {
      marginLeft: '12px'
    }
  },
  flexBox_end: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  submitButton: {
    color: 'primary',
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '160px'
  },
  draftButton: {
    color: theme.colors.proposalColors.buttons.draft,
    border: '0.5px solid ' + theme.colors.proposalColors.buttons.draft,
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '160px'
  },
  proposalData: {
    marginTop: '20px',
    marginLeft: '20px',
    paddingBottom: '20px',
    boxShadow: '6px 9px 43px 0px ' + theme.colors.invoiceColors.shadow,
    [theme.breakpoints.down('xs')]: {
      marginLeft: '12px'
    }
  },
  successSnackbar: {
    backgroundColor: theme.colors.snackbar.success_bg,
    color: theme.colors.snackbar.success_text,
    padding: '12px 16px',
    borderRadius: '8px',
    maxWidth: '200px'
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  icon: {
    color: theme.colors.snackbar.success_text,
    fontSize: '20px'
  },
  title: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  message: {
    fontSize: '14px',
    marginTop: '4px',
    marginLeft: '28px'
  },
  dialogContainer: {
    textAlign: 'center',
    padding: '24px'
  },
  dialogActionsContainer: {
    justifyContent: 'center',
    paddingBottom: '24px'
  },
  iconDialog: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  mainMessage: {
    fontSize: '24px',
    fontWeight: '400',
    marginBottom: '16px'
  },
  secondaryMessage: {
    fontSize: '16px',
    marginBottom: '24px'
  },
  button: {
    color: 'primary',
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '240px'
  },
  leaveButton: {
    color: theme.colors.proposalColors.buttons.draft,
    border: '0.5px solid ' + theme.colors.proposalColors.buttons.draft,
    fontSize: '18px',
    fontWeight: '500',
    textTransform: 'none',
    margin: '0px 20px',
    height: '56px',
    width: '240px'
  }
}))

function getWindowHeight() {
  const [size, setSize] = useState([0, 0])
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight])
    }
    window.addEventListener('resize', updateSize)
    updateSize()
    return () => window.removeEventListener('resize', updateSize)
  }, [])
  return size[1]
}

const CreateProposal = () => {
  const params = useParams()
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const wHeight = getWindowHeight()
  const dispatch = useDispatch()
  const proposalId = new URLSearchParams(location.search).get('id')

  const [disableSubmitButton, setDisableSubmitButton] = useState(false)
  const [proposalHistory, setProposalHistory] = useState([])
  const [data, setData] = useState({
    status: 'draft'
  })
  const [itemTypes, setItemTypes] = useState([])
  const [disableEdition, setDisableEdition] = useState(false)
  const [subtotal, setSubtotal] = useState(0)
  const [taxSum, setTaxSum] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [companyId, setCompanyId] = useState()
  const [userList, setUserList] = useState([])
  const [sfUserList, setSfUserList] = useState([])
  const [locationList, setLocationList] = useState([])
  const [priorityList, setPriorityList] = useState([])
  const [tradeList, setTradeList] = useState([])
  const [proposalPrefix, setProposalPrefix] = useState('')
  const [proposalNumberId, setProposalNumberId] = useState('')
  const [disableDraftButton, setDisableDraftButton] = useState(true)
  const [lastSavedData, setLastSavedData] = useState({})
  const [attachments, setAttachments] = useState([])
  const [openSnackBar, setOpenSnackBar] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [infoMessage, setInfoMessage] = useState('')
  const [infoTitle, setInfoTitle] = useState('')
  const [locationsAssignment, setLocationsAssignment] = useState([])
  const [locationSelected, setLocationSelected] = useState(false)
  const [isIframe] = useState(isRunningInIframe())
  const currentUser = useSelector(state => state.auth.user)
  const loadingFiles = useSelector(state => state.proposals.loadingFiles)

  useEffect(() => {
    dispatch(loadingActions.show())
    const initData = async () => {
      const companyIdTmp = isIframe
        ? currentUser?.userInfo?.lastLoginCompany
        : currentUser?.userInfo?.company_id
      setCompanyId(companyIdTmp)
      const lineItemTypes = await getLineItemTypes()
      const response = await getProposal(proposalId)
      const locations = await getLocationsList()
      setLocationList(locations)

      if (response?.content?.attachments) {
        setAttachments(response?.content?.attachments)
      }
      setLastSavedData(response?.content)
      setData(response?.content)

      setItemTypes(lineItemTypes)
    }
    initData()
  }, [])

  useEffect(() => {
    if (data?.location && !locationSelected) {
      const tradesAvailable = getTradesAvailable(data?.location)
      setTradeList(tradesAvailable)
    }
    if (locationSelected) setLocationSelected(false)
  }, [locationList, data])

  useEffect(() => {
    const fetchCompanyData = async () => {
      const sfUsers = await getSFUserList(companyId)
      const users = await getUserList(companyId)
      const filters = await getSitesAdvancedFiltersInfo(
        isRunningInIframe()
          ? currentUser?.userInfo?.subdomain?.child
          : currentUser?.userInfo?.parent_subdomain
      )
      const priorities = filters?.priorities?.map(prio => {
        return { id: prio.name, label: prio.name }
      })
      setPriorityList(priorities)
      setUserList(users)
      setSfUserList(sfUsers)
    }
    if (typeof companyId !== 'undefined') fetchCompanyData()
  }, [companyId])

  useEffect(() => {
    setDisableDraftButton(isEqual(data, lastSavedData))
    setDisableSubmitButton(!checkSubmitEnabled(data))
    setDisableEdition(!editableProposalStatus.includes(data?.status))
    setProposalHistory(data?.history?.sort((a, b) => b.timestamp - a.timestamp))
    calculateProposalAmount(data?.items)
    formatProposalIdNumber(data?.proposal_id)
    dispatch(loadingActions.hide())
  }, [data])

  const checkSubmitEnabled = tmpData => {
    const status = editableProposalStatus.includes(tmpData?.status)
    // client and location is selected
    const location = tmpData?.location?.id
    // has at least one item
    const items = tmpData?.items?.length > 0
    // all items have amount > 0
    const amounts = tmpData?.items?.every(item => item.amount > 0)
    return status && location && items && amounts
  }

  const handleClose = () => {
    setOpenSnackBar(false)
  }

  const calculateProposalAmount = items => {
    if (items && items.length > 0) {
      const newSubtotal = items.reduce(
        (sum, item) => sum + item.quantity * item.rate,
        0
      )
      const newTaxSum = items.reduce(
        (sum, item) => sum + item.quantity * item.rate * (item.tax / 100),
        0
      )
      const newTotal = newSubtotal + newTaxSum

      setSubtotal(newSubtotal)
      setTaxSum(newTaxSum)
      setTotalAmount(newTotal)
    } else {
      setSubtotal(0)
      setTaxSum(0)
      setTotalAmount(0)
    }
  }

  const formatProposalIdNumber = currentId => {
    const prefix = currentId?.slice(0, 3)
    const numberPart = currentId?.slice(3)
    setProposalPrefix(prefix)
    setProposalNumberId(numberPart)
  }

  const getTradesAvailable = locationData => {
    const tradesAvailable =
      locationsAssignment
        .find(loc => loc.location.id === locationData.id)
        ?.trade_services?.map(trade => trade.name) || []
    const trades = tradesAvailable.map(trade => {
      return { id: trade, label: trade }
    })
    return trades
  }

  const handleChangeData = (field, value) => {
    setData(prevData => {
      if (field === 'location') {
        setLocationSelected(true)
        const tradesAvailable = getTradesAvailable(value)
        setTradeList(tradesAvailable)
        const clientData = {
          id: value?.client_id,
          name: value?.client_name
        }
        delete value.client_id
        delete value.client_name
        return {
          ...prevData,
          location: value,
          client: clientData,
          trade: tradesAvailable?.length > 1 ? '' : tradesAvailable[0]?.id ?? ''
        }
      }
      return prevData[field] === value
        ? prevData
        : { ...prevData, [field]: value }
    })
  }

  const handleUpdateItems = (id, field, value) => {
    setData(prevData => {
      const updatedItems = prevData.items.map(item => {
        if (item.id === id) {
          const updatedItem = {
            ...item,
            [field]: ['rate', 'quantity', 'tax'].includes(field)
              ? parseFloat(value) || 0
              : value
          }
          const quantity = updatedItem.quantity || 0
          const rate = updatedItem.rate || 0
          const taxPercentage = updatedItem.tax || 0
          const taxDecimal = taxPercentage / 100
          // Calculate amount: qty * rate * (1 + tax%)
          updatedItem.amount = parseFloat(
            (quantity * rate * (1 + taxDecimal)).toFixed(2)
          )

          return updatedItem
        }
        return item
      })
      // Update subtotal, tax, and total sums
      const newSubtotal = updatedItems.reduce(
        (sum, item) => sum + item.quantity * item.rate,
        0
      )
      const newTaxSum = updatedItems.reduce(
        (sum, item) => sum + item.quantity * item.rate * (item.tax / 100),
        0
      )
      const newTotal = newSubtotal + newTaxSum
      setSubtotal(newSubtotal)
      setTaxSum(newTaxSum)
      setTotalAmount(newTotal)
      return {
        ...prevData,
        items: updatedItems,
        total_amount: newTotal
      }
    })
  }
  const handleAddItem = () => {
    const newItem = {
      id: nanoid(), // Unique ID
      item: '0',
      description: '',
      quantity: 1,
      tax: 0,
      rate: 0,
      amount: 0,
      factor: 1
    }

    setData(prevData => ({
      ...prevData,
      items: prevData.items ? [...prevData.items, newItem] : [newItem]
    }))
  }
  const handleRemoveItem = (event, id) => {
    event.stopPropagation()

    setData(prevData => {
      const updatedItems = prevData.items.filter(item => item.id !== id)
      calculateProposalAmount(updatedItems)

      return {
        ...prevData,
        items: updatedItems
      }
    })
  }

  const getUserList = async company => {
    const response = await getCompanyUsers(company)
    const ftcUsers = response.filter(
      user => user.roles && user.roles !== 'no_value'
    )
    return ftcUsers.map(user => ({
      id: user.id,
      label: user.firstName + ' ' + user.lastName
    }))
  }

  const getLocationsList = async () => {
    const locationsResponse = await getLocations()
    const validRecords = locationsResponse?.content?.filter(
      record => record?.location?.id
    )

    setLocationsAssignment(validRecords)
    return validRecords?.map(record => ({
      id: record?.location?.id,
      label: record?.name,
      address: record?.address?.address1,
      external_id: record?.id,
      client_id: record?.client_id,
      client_name: record?.client_name,
      trade: record?.trade_services
    }))
  }

  const saveDraft = async () => {
    dispatch(loadingActions.show())
    const currentData = {
      ...data,
      total_amount: totalAmount,
      history: [
        ...data.history,
        {
          action: 'edit',
          timestamp: ~~(Date.now() / 1000)
        }
      ]
    }
    try {
      const response = await updateProposal(proposalId, currentData)
      setInfoMessage(t('proposals.messages.success_message'))
      setInfoTitle(t('proposals.messages.success_title'))
      setOpenSnackBar(true)
      setData(response?.content)
      setLastSavedData(response?.content)
      setDisableDraftButton(true)
      dispatch(loadingActions.hide())
    } catch (error) {
      setDisableDraftButton(false)
      dispatch(loadingActions.hide())
      /* eslint-disable no-console */
      console.error('An error ocurred when saving the draft: ', error)
    }
  }

  const submitProposal = async () => {
    dispatch(loadingActions.show())
    setDisableSubmitButton(true)
    setDisableEdition(true)
    setDisableDraftButton(true)
    // format plain description for SF
    const description = formatHTML(data.description)
    data.plain_description = description
    const response = await updateProposal(proposalId, data, true)
    setData(response?.content)
    setDisableDraftButton(true)
  }

  const formatHTML = htmlText => {
    let description = formatHtmlForExtraction(htmlText)
    if (data?.items?.length) {
      const lineItems = data.items.map(
        item => `${item.item}: ${item.description}`
      )
      description += '\n' + lineItems.join('\n')
    }
    return description
  }

  const FormButtons = ({ draftButtonDisabled, submitButtonDisabled }) => {
    return (
      <Box className={classes.flexBox_end}>
        <Button
          variant="outlined"
          color="primary"
          className={classes.draftButton}
          onClick={async () => await saveDraft()}
          data-testid="save-proposal-button"
          disabled={draftButtonDisabled || loadingFiles}
        >
          {t('proposals.actions.save')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={classes.submitButton}
          onClick={async () => {
            await submitProposal()
          }}
          data-testid="submit-proposal-button"
          disabled={submitButtonDisabled || loadingFiles}
        >
          {t('proposals.actions.submit')}
        </Button>
      </Box>
    )
  }

  const CustomSnackbar = ({
    openSnackBar,
    handleClose,
    title,
    infoMessage
  }) => {
    const classes = useStyles()

    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openSnackBar}
        onClose={handleClose}
        autoHideDuration={7000}
        key="info-message"
      >
        <SnackbarContent
          className={classes.successSnackbar}
          message={
            <div className={classes.contentContainer}>
              <div className={classes.titleRow}>
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className={classes.icon}
                />
                <span className={classes.title}>{title}</span>
              </div>
              <span className={classes.message}>
                <b>
                  {t('proposals.messages.proposal_number').replace(
                    '%NUMBER%',
                    data?.proposal_id
                  )}
                </b>
                {infoMessage}
              </span>
            </div>
          }
        />
      </Snackbar>
    )
  }

  const UnsavedChangesDialog = ({ open, handleClose, handleLeave }) => {
    const classes = useStyles()

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent className={classes.dialogContainer}>
          <FontAwesomeIcon
            icon="fa-solid fa-lightbulb-exclamation-on"
            className={classes.iconDialog}
          />
          <div className={classes.mainMessage}>
            {t('proposals.messages.leave_title')}
            <br />
            {t('proposals.messages.leave_title2')}
          </div>
          <div className={classes.secondaryMessage}>
            {t('proposals.messages.leave_secondary')}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActionsContainer}>
          <Button
            onClick={handleLeave}
            className={classes.leaveButton}
            variant="outlined"
          >
            {t('proposals.labels.leave_page')}
          </Button>
          <Button
            onClick={handleClose}
            color="primary"
            className={classes.button}
            variant="contained"
          >
            {t('proposals.labels.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  const handleLeavePage = () => {
    setOpenDialog(false)
    if (history.goBack.length === 0) {
      if (isRunningInIframe()) {
        history.push(
          '/proposals-iframe/' + params.id + '/' + params.access_token
        )
      } else {
        history.push({ pathname: 'proposals' })
      }
    } else {
      history.goBack()
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  return (
    <Container className={classes.scrollContainer}>
      <Container className={classes.container}>
        <Grid container spacing={2} className={classes.globalHeaderContainer}>
          <Grid item xs={2}>
            <Button
              className={classes.backButton}
              variant="text"
              startIcon={<FontAwesomeIcon icon="fa-solid fa-angle-left" />}
              onClick={() => {
                if (!disableDraftButton && data.status !== 'pending_decision') {
                  setOpenDialog(true)
                } else {
                  handleLeavePage()
                }
              }}
            >
              {t('general.labels.back')}
            </Button>
          </Grid>
        </Grid>
        <Container className={classes.mainContainer}>
          <Container className={classes.leftContainer}>
            <Box className={classes.proposalData} elevation={0}>
              <ProposalHeaderComponent
                data={data}
                handleChangeData={handleChangeData}
                editionDisabled={disableEdition}
                locationList={locationList}
                userList={userList}
                priorityList={priorityList}
                tradeList={tradeList}
                proposalPrefix={proposalPrefix}
                proposalNumberId={proposalNumberId}
                attachments={attachments}
                setAttachments={setAttachments}
                setData={setData}
                sfUserList={sfUserList}
              />
              <Box className={classes.itemsContainer}>
                <ProposalItems
                  itemTag={t('invoices.items.all')}
                  items={data?.items}
                  updateItems={() => {}}
                  itemTypes={itemTypes}
                  editionDisabled={disableEdition}
                  handleUpdateItems={handleUpdateItems}
                  handleAddItem={handleAddItem}
                  handleRemoveItem={handleRemoveItem}
                  disableButtons={disableSubmitButton}
                  subtotal={subtotal}
                  taxSum={taxSum}
                  totalAmount={totalAmount}
                />
              </Box>
            </Box>
          </Container>
          <Container className={classes.rightContainer}>
            <ProposalTimeline
              steps={proposalHistory}
              wHeight={wHeight - 150 + 'px'}
              loading={false}
            />
          </Container>
          {editableProposalStatus.includes(data?.status) && (
            <Container className={classes.bottomContainer}>
              <FormButtons
                draftButtonDisabled={disableDraftButton}
                submitButtonDisabled={disableSubmitButton}
              />
            </Container>
          )}
        </Container>
      </Container>
      <CustomSnackbar
        openSnackBar={openSnackBar}
        handleClose={handleClose}
        title={infoTitle}
        infoMessage={infoMessage}
      />
      <UnsavedChangesDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        handleLeave={handleLeavePage}
      />
    </Container>
  )
}

export default CreateProposal
